import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout, selectCurrentUser, selectUserRole, selectCompanyName } from '../../store/slices/authSlice';
import clsx from 'clsx';
import { useSessionTimeout } from '../../hooks/useSessionTimeout';
import SessionTimeoutModal from '../common/SessionTimeoutModal';
import NotificationBell from '../common/NotificationBell';
import { adminApi, vendorApi, clientApi } from '../../services/api';

/* ── Nav icons (inline SVG so no extra deps) ──────────────────────────── */
const Icon = ({ path, ...p }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
    strokeLinecap="round" strokeLinejoin="round" {...p}>
    {path}
  </svg>
);

const ICONS = {
  dashboard:       <><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></>,
  'finished-goods':<><path d="M20 7H4a2 2 0 00-2 2v9a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z"/><path d="M16 3H8l-2 4h12l-2-4z"/></>,
  review:          <><path d="M9 12l2 2 4-4"/><path d="M21 12c0 4.97-4.03 9-9 9S3 16.97 3 12 7.03 3 12 3s9 4.03 9 9z"/></>,
  users:           <><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></>,
  assignments:     <><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><path d="M9 12h6M9 16h4"/></>,
  requests:        <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/></>,
  companies:       <><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><path d="M9 22V12h6v10"/></>,
  inquiries:       <><path d="M8 10h.01M12 10h.01M16 10h.01"/><path d="M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3v-3z"/></>,
  'bobs-dashboard':<><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></>,
};

/**
 * Navigation links keyed by role.
 *
 * Rules:
 *  - *_VIEWER  : no "User Management" or write-action-only pages
 *  - *_USER    : full operational nav but no user/company admin pages
 *  - *_ADMIN   : full nav including user management
 *  - PLATFORM_SUPER_ADMIN / CLIENT_VENDOR_ADMIN: full platform navigation
 */
const FULL_PLATFORM_NAV_LINKS = [
  { to: '/platform/dashboard',      label: 'Dashboard',       icon: 'dashboard'       },
  { to: '/platform/finished-goods', label: 'Finished Goods',  icon: 'finished-goods'  },
  { to: '/platform/review',         label: 'Review Queue',    icon: 'review'          },
  { to: '/platform/companies',      label: 'Companies',       icon: 'companies'       },
  { to: '/platform/users',          label: 'OmniDrive Users', icon: 'users'           },
  { to: '/platform/inquiries',      label: 'Leads',           icon: 'inquiries'       },
  { to: '/platform/bobs-dashboard', label: "BOB's Dashboard", icon: 'bobs-dashboard' },
];

const NAV_LINKS = {
  // ── Platform ───────────────────────────────────────────────────────
  PLATFORM_SUPER_ADMIN: FULL_PLATFORM_NAV_LINKS,
  PLATFORM_ADMIN: [
    { to: '/platform/dashboard',      label: 'Dashboard',       icon: 'dashboard'       },
    { to: '/platform/finished-goods', label: 'Finished Goods',  icon: 'finished-goods'  },
    { to: '/platform/review',         label: 'Review Queue',    icon: 'review'          },
    { to: '/platform/companies',      label: 'Companies',       icon: 'companies'       },
    { to: '/platform/users',          label: 'OmniDrive Users',  icon: 'users'           },
    { to: '/platform/inquiries',      label: 'Leads',           icon: 'inquiries'       },
  ],
  PLATFORM_USER: [
    { to: '/platform/dashboard',      label: 'Dashboard',       icon: 'dashboard'       },
    { to: '/platform/finished-goods', label: 'Finished Goods',  icon: 'finished-goods'  },
    { to: '/platform/review',         label: 'Review Queue',    icon: 'review'          },
    { to: '/platform/companies',      label: 'Companies',       icon: 'companies'       },
  ],
  PLATFORM_VIEWER: [
    { to: '/platform/dashboard',      label: 'Dashboard',       icon: 'dashboard'       },
    { to: '/platform/finished-goods', label: 'Finished Goods',  icon: 'finished-goods'  },
    { to: '/platform/companies',      label: 'Companies',       icon: 'companies'       },
  ],

  // ── Client-Vendor Admin (full Super Admin platform access) ──────────
  CLIENT_VENDOR_ADMIN: FULL_PLATFORM_NAV_LINKS,

  // ── Vendor ─────────────────────────────────────────────────────────
  VENDOR_ADMIN: [
    { to: '/vendor/dashboard',    label: 'Dashboard',       icon: 'dashboard'    },
    { to: '/vendor/assignments',  label: 'Assignments',     icon: 'assignments'  },
    { to: '/vendor/users',        label: 'User Management', icon: 'users'        },
  ],
  VENDOR_USER: [
    { to: '/vendor/dashboard',    label: 'Dashboard',       icon: 'dashboard'    },
    { to: '/vendor/assignments',  label: 'Assignments',     icon: 'assignments'  },
  ],
  VENDOR_VIEWER: [
    { to: '/vendor/dashboard',    label: 'Dashboard',       icon: 'dashboard'    },
    { to: '/vendor/assignments',  label: 'Assignments',     icon: 'assignments'  },
  ],

  // ── Client ─────────────────────────────────────────────────────────
  CLIENT_ADMIN: [
    { to: '/client/dashboard', label: 'Dashboard',       icon: 'dashboard' },
    { to: '/client/requests',  label: 'My Requests',     icon: 'requests'  },
    { to: '/client/users',     label: 'User Management', icon: 'users'     },
    { to: '/client/bobs-dashboard', label: "BOB's Dashboard", icon: 'bobs-dashboard' },
  ],
  CLIENT_USER: [
    { to: '/client/dashboard', label: 'Dashboard',   icon: 'dashboard' },
    { to: '/client/requests',  label: 'My Requests', icon: 'requests'  },
    { to: '/client/bobs-dashboard', label: "BOB's Dashboard", icon: 'bobs-dashboard' },
  ],
  CLIENT_VIEWER: [
    { to: '/client/dashboard', label: 'Dashboard',   icon: 'dashboard' },
    { to: '/client/requests',  label: 'My Requests', icon: 'requests'  },
    { to: '/client/bobs-dashboard', label: "BOB's Dashboard", icon: 'bobs-dashboard' },
  ],
};

/** Portal-level colour / label (shared across all roles in that portal). */
const PORTAL_META = {
  platform: { label: 'Admin Portal',  accent: '#3b82f6' },
  vendor:   { label: 'Vendor Portal', accent: '#f59e0b' },
  client:   { label: 'Account Portal', accent: '#10b981' },
};

/** Always show OMNiDRIVE regardless of portal or company. */
function useBrandName() {
  return 'OMNiDRIVE';
}

/** Human-readable role label shown in the top-bar badge. */
const ROLE_LABELS = {
  PLATFORM_SUPER_ADMIN: 'Super Admin',
  CLIENT_VENDOR_ADMIN:  'Client Vendor Admin',
  PLATFORM_ADMIN:       'Admin',
  PLATFORM_USER:        'User',
  PLATFORM_VIEWER:      'Viewer',
  CLIENT_ADMIN:         'Account Admin',
  CLIENT_USER:          'Account User',
  CLIENT_VIEWER:        'Account Viewer',
  VENDOR_ADMIN:         'Vendor Admin',
  VENDOR_USER:          'Vendor User',
  VENDOR_VIEWER:        'Viewer',
};

/* ── OmniDrive lightning bolt mark ───────────────────────────────────── */
function OmniDriveMark({ size = 32 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width={size} height={size} style={{ flexShrink: 0 }}>
      <polygon points="23,4 12,22 19,22 16,36 27,18 20,18" fill="#f59e0b" />
    </svg>
  );
}

export default function AppLayout({ portal }) {
  const dispatch     = useDispatch();
  const navigate     = useNavigate();
  const user         = useSelector(selectCurrentUser);
  const role         = useSelector(selectUserRole);
  const companyName  = useSelector(selectCompanyName);
  const [open, setOpen] = useState(true);
  const links      = NAV_LINKS[role]  || [];
  const meta       = PORTAL_META[portal] || PORTAL_META.client;
  const brandName  = useBrandName();

  // ── Session timeout (60 min idle → auto-logout) ──────────────────────────
  const { showWarning, secondsLeft, resetTimer, performLogout } = useSessionTimeout(true);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const initials = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .map(w => w[0])
    .join('')
    .toUpperCase() || user?.fullName?.[0]?.toUpperCase() || 'U';

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#f8fafc' }}>

      {/* ── Sidebar ── */}
      <aside
        className={clsx(
          'relative flex flex-col transition-all duration-300 ease-in-out flex-shrink-0 border-r border-gray-100',
          open ? 'w-60' : 'w-[68px]'
        )}
        style={{ background: '#ffffff' }}
      >
        {/* Logo */}
        <div className={clsx(
          'flex items-center gap-3 px-4 border-b border-gray-100',
          open ? 'py-4' : 'py-4 justify-center'
        )}>
          {open ? (
            <div className="min-w-0 flex flex-col gap-1">
              <img
                src="/omnidrive-logo-cropped.png"
                alt="OMNiDRIVE"
                className="h-8 w-auto object-contain"
              />
              <p className="text-xs font-medium truncate" style={{ color: meta.accent }}>{meta.label}</p>
            </div>
          ) : (
            <OmniDriveMark size={28} />
          )}
        </div>

        {/* Nav */}
        <nav className={clsx('flex-1 py-4 space-y-0.5', open ? 'px-3' : 'px-2')}>
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              title={!open ? link.label : undefined}
              className={({ isActive }) => clsx(
                'flex items-center gap-3 rounded-xl text-sm font-medium transition-all duration-150 group',
                open ? 'px-3 py-2.5' : 'px-2 py-2.5 justify-center',
                isActive
                  ? 'text-gray-900'
                  : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
              )}
              style={({ isActive }) => isActive
                ? { background: `linear-gradient(135deg, ${meta.accent}22, ${meta.accent}33)`, color: meta.accent }
                : {}}
            >
              {({ isActive }) => (
                <>
                  <svg
                    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
                    strokeLinecap="round" strokeLinejoin="round"
                    className="flex-shrink-0 h-[18px] w-[18px]"
                    style={{ color: isActive ? meta.accent : 'inherit' }}
                  >
                    {ICONS[link.icon]}
                  </svg>
                  {open && <span className="truncate">{link.label}</span>}
                  {open && isActive && (
                    <span className="ml-auto h-1.5 w-1.5 rounded-full flex-shrink-0"
                      style={{ background: meta.accent }} />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        <div className={clsx('border-t border-gray-100 p-3', !open && 'flex flex-col items-center gap-2')}>
          {open ? (
            <div className="flex items-center gap-3 mb-2 px-1">
              <div
                className="h-8 w-8 rounded-xl flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                style={{ background: `linear-gradient(135deg,${meta.accent},${meta.accent}88)` }}
              >
                {initials}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-gray-900 text-xs font-semibold truncate">{user?.fullName || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}</p>
                <p className="text-gray-400 text-xs truncate">{user?.email}</p>
              </div>
            </div>
          ) : (
            <div
              className="h-8 w-8 rounded-xl flex items-center justify-center text-white text-xs font-bold"
              style={{ background: `linear-gradient(135deg,${meta.accent},${meta.accent}88)` }}
              title={user?.fullName}
            >
              {initials}
            </div>
          )}
          <button
            onClick={handleLogout}
            title="Logout"
            className={clsx(
              'flex items-center gap-2 text-gray-500 hover:text-red-500 rounded-lg px-2 py-1.5 text-xs font-medium transition-colors hover:bg-red-50',
              open ? 'w-full' : 'justify-center w-full'
            )}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
              strokeLinecap="round" className="h-4 w-4 flex-shrink-0">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
            </svg>
            {open && 'Sign out'}
          </button>
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setOpen(!open)}
          className="absolute -right-3 top-[72px] h-6 w-6 rounded-full border border-gray-200 bg-white shadow-sm flex items-center justify-center text-gray-400 hover:text-gray-800 hover:border-gray-400 transition-colors z-10"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
            strokeLinecap="round" className="h-3 w-3">
            {open ? <path d="M15 18l-6-6 6-6" /> : <path d="M9 18l6-6-6-6" />}
          </svg>
        </button>
      </aside>

      {/* ── Main ── */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-100 px-6 py-3 flex items-center justify-end flex-shrink-0">
          <div className="flex items-center gap-3">
            {/* Notification bell — scoped to current portal */}
            <NotificationBell
              portal={portal}
              api={portal === 'platform' ? adminApi : portal === 'vendor' ? vendorApi : clientApi}
            />
            {/* Role badge */}
            <span
              className="px-2.5 py-0.5 rounded-full text-xs font-semibold border"
              style={{ color: meta.accent, borderColor: `${meta.accent}44`, background: `${meta.accent}11` }}
            >
              {ROLE_LABELS[role] || role?.replace(/_/g, ' ')}
            </span>

            {/* Avatar */}
            <div
              className="h-8 w-8 rounded-xl flex items-center justify-center text-white text-xs font-bold"
              style={{ background: `linear-gradient(135deg,${meta.accent},${meta.accent}88)` }}
            >
              {initials}
            </div>
            <div className="text-sm hidden sm:block">
              <p className="font-semibold text-gray-900 leading-tight">{user?.fullName || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}</p>
              <p className="text-xs text-gray-400 leading-tight">{user?.email}</p>
            </div>

            {/* Logout button — always visible in header */}
            <button
              onClick={handleLogout}
              title="Sign out"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-red-600 hover:bg-red-50 border border-gray-200 hover:border-red-200 rounded-lg transition-colors ml-1"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
                strokeLinecap="round" className="h-3.5 w-3.5 flex-shrink-0">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
              </svg>
              <span>Sign out</span>
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>

      {/* ── Session timeout warning modal ─────────────────────────────────── */}
      {showWarning && (
        <SessionTimeoutModal
          secondsLeft={secondsLeft}
          onStay={resetTimer}
          onLogout={performLogout}
        />
      )}
    </div>
  );
}
